import React, { useEffect, useMemo, useState } from "react";
import Footer from "../../components/layouts/Footer";
import MailingList from "../../components/MailingList";
import SortDropdown from "../../components/SortDropdown";
import SegmentedControl from "../../components/SegmentedControl";
import ArtworkCard from "../../components/ArtworkCard";
import CategoryChips from "../../components/CategoryChips";
import { ArtworkCardProps } from "../../types";
import { Link, useParams } from "react-router-dom";
import { usePersistStore, useStore } from "../../store";
import { AD_TYPES } from "../../utils/enums";
import RecentArtworksSwipper from "../../components/RecentArtworksSwipper";
import { Skeleton } from "boneyard-js/react";
import { useCategory, useAdSearchInfinite } from "../../hooks/queries";

// Titles for the special (non-category) sections. Anything else is a real
// category id, whose name we look up via the cached useCategory hook.
const SPECIAL_TITLES: Record<string, string> = {
  "open-for-bids": "Open for bids",
  upcoming: "Upcoming bids",
  popular: "Popular bids",
  others: "Others",
  all: "All",
};

function Explore() {
  const { id } = useParams();
  const [sorting, setSorting] = useState<string>("price-low-to-high");
  const { setErrorMessage, keyword, priceRange, radius, mediumIds } = useStore(
    (s: any) => s
  );
  const { currentLocation } = usePersistStore((s: any) => s);
  const [title, setTitle] = useState<string>("Open for bids");
  const [isPopular, setIsPopular] = useState<boolean>(false);
  const [bidState, setBidState] = useState<string>(AD_TYPES.OPEN_FOR_BIDS);

  // True when the view should expose the open/upcoming/sold segmented
  // filter and forward `bidState` to the API. Category views (id is a
  // mongo hex) and the "others" bucket both mix ads across bid states,
  // so they need this control. The other special sections
  // (open-for-bids, upcoming, popular, etc.) already filter by bid
  // state via `type`.
  const supportsBidState =
    !!id && (!Object.values(AD_TYPES).includes(id) || id === AD_TYPES.OTHERS);

  const searchParams = {
    type: id || AD_TYPES.OPEN_FOR_BIDS,
    bidState: supportsBidState ? bidState : undefined,
    sorting,
    keyword,
    range: priceRange,
    radius,
    mediumIds,
    location: currentLocation,
  };
  const {
    data,
    isLoading,
    error: searchError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useAdSearchInfinite(searchParams);

  useEffect(() => {
    if (searchError) setErrorMessage((searchError as Error).message);
  }, [searchError, setErrorMessage]);

  const list: ArtworkCardProps[] = useMemo(
    () =>
      (data?.pages || [])
        .flatMap((p: any) => p?.ads || [])
        .map((tt: any) => ({
          _id: tt?._id,
          color: true,
          imageUrl: tt?.images?.[0],
          category: tt?.categoryId?.name,
          subCategory: tt?.subCategoryId?.name,
          views: tt?.views || 0,
          title: tt?.title,
          ownerImage: tt?.userId?.fileUrl,
          ownerName: tt?.userId?.name,
          ownerId: tt?.userId?._id,
          biddingStarts: tt?.biddingStarts,
          biddingEnds: tt?.biddingEnds,
          price: tt?.price,
          showBidNowButton: true,
          showRemoveFavouriteButton: false,
          status: tt?.status,
          distance: tt?.distance,
        })),
    [data]
  );
  const isReady = !isLoading;

  const categoryIdForTitle = id && !(id in SPECIAL_TITLES) ? id : undefined;
  const { data: titleCategory } = useCategory(categoryIdForTitle);

  useEffect(() => {
    if (!id) return setTitle("Open for bids");
    if (id === "popular") setIsPopular(true);
    if (id in SPECIAL_TITLES) return setTitle(SPECIAL_TITLES[id]);
    if (titleCategory?.name) setTitle(titleCategory.name);
  }, [id, titleCategory]);

  return (
    <>
      <div className="container px-md-0 px-3">
        <div className="row gx-md-0">
          <div className="breadCrumb mt-4 mb-3">
            <div className="d-flex gap-3">
              <Link to="/">
                <p className="m-0">Home</p>
              </Link>
              <span>/</span>
              <Link to="">
                <h4>{title}</h4>
              </Link>
            </div>
          </div>
          <div className="col-md-12 px-md-0 px-3">
            <RecentArtworksSwipper />
          </div>
          <div className="col-md-12 px-md-0 px-3 mt-4">
            <CategoryChips />
          </div>
          <div className="col-md-12 py-4">
            <div className="py-4">
              <div className="d-flex align-items-center customTitle pb-3 px-0 justify-content-between flex-wrap gap-2">
                <h2 className="text-center">{title}</h2>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {supportsBidState && (
                    <SegmentedControl
                      ariaLabel="Filter by bid state"
                      value={bidState}
                      onChange={setBidState}
                      options={[
                        { value: AD_TYPES.OPEN_FOR_BIDS, label: "Open for bids" },
                        { value: AD_TYPES.UPCOMING, label: "Upcoming" },
                        { value: AD_TYPES.SOLD, label: "Sold" },
                      ]}
                    />
                  )}
                  <SortDropdown
                    value={sorting}
                    onChange={setSorting}
                    options={[
                      ...(isPopular
                        ? [{ value: "popular-first", label: "Popular first" }]
                        : []),
                      { value: "recent-first", label: "Newest first" },
                      { value: "price-low-to-high", label: "Price low to high" },
                      { value: "price-high-to-low", label: "Price high to low" },
                    ]}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              {!isReady &&
                Array.from({ length: 9 }).map((_, i) => (
                  <div className="col-12 col-sm-6 col-md-4 mb-5" key={`sk-${i}`}>
                    <Skeleton name="artwork-card" loading={true}>
                      <span style={{ display: "block", aspectRatio: "3 / 4" }} />
                    </Skeleton>
                  </div>
                ))}
              {isReady && list?.length === 0 && (
                <div className="col-12">
                  <div className="searchEmpty">
                    <div className="searchEmpty__icon">
                      <i className="bi bi-search" aria-hidden="true" />
                    </div>
                    <h3 className="searchEmpty__title">No artworks found</h3>
                    <p className="searchEmpty__subtitle">
                      Nothing matches the current filters in this view. Try
                      switching the bid state, clearing filters, or browsing a
                      different category.
                    </p>
                  </div>
                </div>
              )}
              {isReady &&
                list?.map((slide: ArtworkCardProps, index: number) => (
                  <div className="col-12 col-sm-6 col-md-4 mb-5" key={index}>
                    <ArtworkCard {...slide} />
                  </div>
                ))}
            </div>
            {isReady && list?.length > 0 && hasNextPage && (
              <button
                className="mainBtn px-5 mx-auto my-4"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                style={{ opacity: isFetchingNextPage ? 0.7 : 1 }}
              >
                {isFetchingNextPage ? "Loading…" : "Load More"}
              </button>
            )}
          </div>
        </div>
      </div>
      {/* <div className="container pt-4">
        <div className="row mx-0">
          <div className="col-md-11 mx-auto mb-4">
            <MailingList />
          </div>
        </div>
      </div> */}
      <Footer />
    </>
  );
}

export default Explore;
